import { useEffect } from 'react';
import type { GetStaticPropsContext } from 'next';
import { useQuery } from '@sanity/react-loader';
import { groq } from 'next-sanity';

import { fetchBlogPostPaths } from '#/lib/sanity/fetchers/fetchBlogPostPaths';
import { BlogPostProps as BlogPostPropsSchema } from '#/lib/sanity/fetchers/fetchBlogPostProps';
import {
  checkPageSchema,
  type NextPageData,
} from '#/lib/sanity/fetchers/helpers';
import {
  loadQuery,
  loadQueryOptions,
  type QueryResponseInitial,
} from '#/lib/sanity/loader';
import { blogPost, SanityBlogPost } from '#/lib/sanity/queries/blogPost';
import { site } from '#/lib/sanity/queries/site';
import { useAnalytics } from '#/lib/segment';
import { addViewedAlgoliaObjectID } from '#/lib/viewed-algolia-objects';
import { AuthorPreview } from '#/components/AuthorPreview';
import { Footnotes } from '#/components/Footnotes';
import { Headline } from '#/components/Headline';
import { Media } from '#/components/Media';
import { RelatedContent } from '#/components/RelatedContent';
import { RichText } from '#/components/RichText';
import { ArticleSchema } from '#/components/SeoSchema/ArticleSchema';
import { BreadcrumbSchema } from '#/components/SeoSchema/BreadcrumbSchema';
import { ShareButtons } from '#/components/ShareButtons';

export const config = {
  maxDuration: 30,
};

export async function getStaticPaths() {
  const paths = await fetchBlogPostPaths();

  return {
    paths,
    fallback: 'blocking',
  };
}

const blogPostQuery = groq`{
  'page': ${blogPost},
  'site': ${site},
}`;

type BlogPostQueryResult = Omit<
  NextPageData<typeof SanityBlogPost>,
  'draftMode'
>;

export async function getStaticProps({
  params,
  draftMode,
}: GetStaticPropsContext<{ slug: string }>) {
  const initial = await loadQuery<BlogPostQueryResult>(
    blogPostQuery,
    { slug: params!.slug },
    loadQueryOptions({ draftMode }),
  );
  const data = await checkPageSchema<typeof SanityBlogPost>({
    schema: BlogPostPropsSchema,
    result: initial.data,
    draftMode,
    context: { callerName: 'blog post getStaticProps', slug: params!.slug },
  });
  const props = { ...data, draftMode: !!draftMode };

  if (!props.page) {
    return {
      notFound: true,
    };
  }

  return { props: { ...props, initial } };
}

export default function BlogPost(
  props: NextPageData<typeof SanityBlogPost> & {
    initial: QueryResponseInitial<BlogPostQueryResult>;
  },
) {
  const initialPost = props.page;
  const { data } = useQuery<BlogPostQueryResult>(
    blogPostQuery,
    { slug: initialPost?.href.replace('/blog/', '') ?? '' },
    { initial: props.initial },
  );
  const post = data.page ?? initialPost;
  const analytics = useAnalytics();

  // Track the viewed post in local storage - used for Algolia search re-ranking
  useEffect(() => {
    if (!post) return;

    addViewedAlgoliaObjectID({
      objectID: post._id,
      algoliaObjectType: 'POSTS',
    });
    analytics.track('Viewed Blog Post', {
      blogPostTitle: post.title,
      blogPostCategories: post.category.map((category) => category.title),
      blogPostId: post._id,
    });
  }, [post, analytics]);

  if (!post) return null;

  const authors = post.author;

  return (
    <>
      <BreadcrumbSchema
        items={[
          { name: 'Blog', path: '/blog' },
          { name: post.title, path: post.href },
        ]}
      />
      <ArticleSchema {...post} />
      <div className="p-main mb-50 pt-20 m:mb-100 m:pt-30">
        <div className="mx-auto max-w-980">
          <Headline
            as="h1"
            value={post.title}
            variant={3}
            className="mb-10 text-pretty m:mb-30"
          />
          <p className="mb-25 mt-15 flex text-12 text-grayscale-05 m:mt-30 l:mb-60 l:text-14">
            {post.readTime} min read
          </p>
          <div className="relative">
            <Media
              media={post.thumbnailImage}
              className="mb-40 aspect-[16/9] rounded-15 border-1 border-grayscale-03 m:mb-60 m:rounded-25 mx-auto"
              sizes={['100vw', { m: '1000px' }]}
              mediaClassName="object-cover"
              aspect={false}
              placeholder={true}
            />
          </div>
          <RichText value={post.content} />
          <hr className="mb-30 mt-60 border-t-1 border-grayscale-03" />
          <div className="flex justify-between gap-x-15">
            <AuthorPreview authors={authors} publishedAt={post.publishedAt} />
            <ShareButtons
              title={post.title}
              href={`/blog/${post.href}`}
              className="pt-4 text-right"
            />
          </div>
        </div>
      </div>
      {post.relatedContent ? <RelatedContent {...post.relatedContent} /> : null}
      {post.footnotes ? <Footnotes {...post.footnotes} /> : null}
    </>
  );
}
